// Sidebar navigation links: grouped/standalone routes with role filtering
// (minRight) and pending-count badges, in Horizon styling.
import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Box, Collapse, Flex, Text, useColorModeValue } from "@chakra-ui/react";
import { MdExpandLess, MdExpandMore } from "react-icons/md";
import { isChildVisible } from "horizon/routeUtils";

/**
 * Small orange counter badge shown when a positive count is provided.
 * @param {Object} props - count
 * @returns {React.ReactElement|null}
 */
function NavBadge({ count }) {
  if (!count || count <= 0) return null;
  return (
    <Flex align="center" justify="center" minW="20px" h="20px" px="6px"
      borderRadius="full" bg="orange.500" color="white" fontSize="11px" fontWeight="700">
      {count > 99 ? "99+" : count}
    </Flex>
  );
}

/**
 * Single navigation leaf (link to one route) with active-state styling.
 * @param {Object} props - route, isChild, count, onNavigate
 * @returns {React.ReactElement}
 */
function SidebarLeaf({ route, isChild, count, onNavigate }) {
  const location = useLocation();
  const active = location.pathname === route.layout + route.path;
  const activeText = useColorModeValue("navy.700", "white");
  const inactiveText = useColorModeValue("secondaryGray.700", "secondaryGray.400");
  const hoverBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const brand = useColorModeValue("brand.500", "brand.400");
  const Icon = route.icon;
  return (
    <NavLink to={route.layout + route.path} onClick={onNavigate}>
      <Flex align="center" gap="14px" py="9px" ps={isChild ? "26px" : "14px"} pe="14px"
        borderRadius="10px" transition="background 0.15s ease" _hover={{ bg: hoverBg }}>
        <Box as="span" display="flex" fontSize="20px" color={active ? brand : inactiveText}>
          <Icon />
        </Box>
        <Text flex="1" fontSize="sm" whiteSpace="nowrap"
          color={active ? activeText : inactiveText} fontWeight={active ? "bold" : "medium"}>
          {route.name}
        </Text>
        <NavBadge count={count} />
        <Box h="28px" w="4px" borderRadius="5px" bg={active ? brand : "transparent"} />
      </Flex>
    </NavLink>
  );
}

/**
 * Collapsible group: header toggles a list of role-filtered child leaves.
 * @param {Object} props - route, userRight, badges, isOpen, onToggle, onNavigate
 * @returns {React.ReactElement|null}
 */
function SidebarGroupBlock({ route, userRight, badges, isOpen, onToggle, onNavigate }) {
  const textColor = useColorModeValue("navy.700", "white");
  const hoverBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const visible = route.children.filter((c) => isChildVisible(c, userRight));
  if (visible.length === 0) return null;
  const Icon = route.icon;
  return (
    <Box mb="2px">
      <Flex align="center" gap="14px" py="10px" px="14px" cursor="pointer" onClick={onToggle}
        borderRadius="10px" transition="background 0.15s ease" _hover={{ bg: hoverBg }}>
        <Box as="span" display="flex" fontSize="20px" color={textColor}><Icon /></Box>
        <Text flex="1" fontSize="sm" fontWeight="bold" color={textColor}>{route.name}</Text>
        <Box as="span" display="flex" fontSize="18px" color={textColor}>
          {isOpen ? <MdExpandLess /> : <MdExpandMore />}
        </Box>
      </Flex>
      <Collapse in={isOpen} animateOpacity>
        {visible.map((child, idx) => (
          <SidebarLeaf key={idx} route={child} isChild count={badges?.[child.path] || 0}
            onNavigate={onNavigate} />
        ))}
      </Collapse>
    </Box>
  );
}

/**
 * Renders the full sidebar navigation from the routes config.
 * @param {Object} props - routes, userRight, badges, onNavigate
 * @returns {React.ReactElement}
 */
export default function SidebarLinks({ routes, userRight, badges, onNavigate }) {
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    routes.forEach((route) => {
      if (route.type !== "group") return;
      const active = route.children.some((c) => location.pathname === c.layout + c.path);
      if (active) setOpenGroups((prev) => ({ ...prev, [route.name]: true }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <Box>
      {routes.map((route, idx) => (route.type === "group" ? (
        <SidebarGroupBlock key={idx} route={route} userRight={userRight} badges={badges}
          isOpen={!!openGroups[route.name]}
          onToggle={() => setOpenGroups((p) => ({ ...p, [route.name]: !p[route.name] }))}
          onNavigate={onNavigate} />
      ) : (
        <SidebarLeaf key={idx} route={route} isChild={false}
          count={badges?.[route.path] || 0} onNavigate={onNavigate} />
      )))}
    </Box>
  );
}
